import { logger } from "@/infrastructure/services/LoggerService";
import { getApiErrorStatus } from "@/shared/utils/apiErrorStatus";
import { isPermanentFailureStatus } from "../infrastructure/api/httpStatus";
const FAST_PHASE_MS = 30000;
const MEDIUM_PHASE_MS = 5 * 60000;
const FAST_INTERVAL = 1000;
const MEDIUM_INTERVAL = 2000;
const SLOW_INTERVAL = 5000;
const MAX_CONSECUTIVE_FAILURES = 3;
function nextDelay(elapsed) {
    if (elapsed < FAST_PHASE_MS)
        return FAST_INTERVAL;
    if (elapsed < MEDIUM_PHASE_MS)
        return MEDIUM_INTERVAL;
    return SLOW_INTERVAL;
}
export function useRequestPolling(opts) {
    /** The request being polled; `null` while stopped. */
    let requestId = null;
    let lastRequestId = null;
    let timer = null;
    let abortCtrl = null;
    let startedAt = 0;
    let consecutiveFailures = 0;
    function clearTimer() {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    }
    function abortInFlight() {
        if (abortCtrl) {
            abortCtrl.abort();
            abortCtrl = null;
        }
    }
    function schedule(delay) {
        clearTimer();
        if (requestId === null)
            return;
        timer = setTimeout(() => void tick(), delay);
    }
    function onVisibilityChange() {
        if (!document.hidden) {
            schedule(0);
        }
    }
    async function tick() {
        if (requestId === null || document.hidden)
            return;
        abortInFlight();
        abortCtrl = new AbortController();
        const currentCtrl = abortCtrl;
        try {
            const request = await opts.fetchRequest(requestId, currentCtrl.signal);
            if (currentCtrl.signal.aborted)
                return;
            consecutiveFailures = 0;
            opts.onUpdate(request);
            if (opts.isSettled(request)) {
                stop();
                return;
            }
            schedule(nextDelay(Date.now() - startedAt));
        }
        catch (err) {
            if (currentCtrl.signal.aborted)
                return;
            consecutiveFailures += 1;
            logger.debug("request polling read failed", err);
            if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES || isPermanentFailureStatus(getApiErrorStatus(err))) {
                stop();
                opts.onError(err);
                return;
            }
            schedule(2000 * Math.pow(2, consecutiveFailures - 1));
        }
    }
    function start(id) {
        stop();
        requestId = id;
        lastRequestId = id;
        startedAt = Date.now();
        consecutiveFailures = 0;
        document.addEventListener("visibilitychange", onVisibilityChange);
        schedule(0);
    }
    function stop() {
        requestId = null;
        clearTimer();
        abortInFlight();
        document.removeEventListener("visibilitychange", onVisibilityChange);
    }
    function retry() {
        if (lastRequestId !== null) {
            start(lastRequestId);
        }
    }
    return { start, stop, retry };
}
